/**
 * DV-PROJ-001: Character Profiles Section Component
 *
 * Manages character profiles for a project including:
 * - Character profile CRUD
 * - Seed image upload and generation
 * - 9:16 preview display
 * - Default character selection
 */

import React, { useState } from 'react';
import {
  Plus,
  Users,
  Star,
  StarOff,
  Wand2,
  Upload,
  MoreHorizontal,
  Edit2,
  Trash2,
  ImageIcon
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useCharacterProfilesQuery,
  useUpdateCharacterProfileMutation,
  useDeleteCharacterProfileMutation
} from '@/hooks/useProjectsQuery';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import { Skeleton } from '@/components/ui/LoadingSkeletons';
import CreateCharacterModal from '../modals/CreateCharacterModal';
import SeedGenerationModal from '../modals/SeedGenerationModal';
import type { Project, CharacterProfile } from '@/types/projects';

interface CharacterProfilesSectionProps {
  project: Project;
}

interface CharacterCardProps {
  character: CharacterProfile;
  onEdit: (character: CharacterProfile) => void;
  onDelete: (character: CharacterProfile) => void;
  onGenerateSeed: (character: CharacterProfile) => void;
  onToggleDefault: (character: CharacterProfile) => void;
}

const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  onEdit,
  onDelete,
  onGenerateSeed,
  onToggleDefault
}) => {
  const [showActions, setShowActions] = useState(false);

  // Get the first seed image for preview
  const previewImage = character.seedImageUrls[0];

  return (
    <div className="relative group">
      <HolographicCard
        glow={character.isDefault ? 'cyan' : 'blue'}
        intensity={character.isDefault ? 'medium' : 'subtle'}
        className="h-full"
      >
        <CardContent className="p-4">
          {/* Preview Image */}
          <div className="aspect-[9/16] bg-black/50 border border-white/10 rounded-lg mb-3 overflow-hidden relative">
            {previewImage ? (
              <img
                src={previewImage}
                alt={character.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <ImageIcon className="w-8 h-8 text-white/20" />
              </div>
            )}

            {/* Default Star Badge */}
            {character.isDefault && (
              <div className="absolute top-2 left-2 bg-cyan-500/20 border border-cyan-500/50 rounded-full p-1">
                <Star className="w-3 h-3 text-cyan-400 fill-current" />
              </div>
            )}

            {/* Image Count Badge */}
            {character.seedImageUrls.length > 0 && (
              <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1">
                <span className="text-xs text-white/80 font-mono">
                  {character.seedImageUrls.length}
                </span>
              </div>
            )}
          </div>

          {/* Character Info */}
          <div className="mb-3">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-sm font-medium text-white/90 truncate flex-1">
                {character.name}
              </h3>

              {/* Actions Menu */}
              <div className="relative ml-2">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className={cn(
                    'p-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-300',
                    'hover:bg-white/10 text-white/60 hover:text-white/80',
                    showActions && 'opacity-100 bg-white/10'
                  )}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>

                {showActions && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActions(false)}
                    />
                    <div className="absolute right-0 top-8 z-20 bg-black/90 backdrop-blur-xl border border-white/20 rounded-lg shadow-xl min-w-[140px]">
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onEdit(character);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onToggleDefault(character);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2"
                        >
                          {character.isDefault ? (
                            <>
                              <StarOff className="w-4 h-4" />
                              Unset Default
                            </>
                          ) : (
                            <>
                              <Star className="w-4 h-4" />
                              Set Default
                            </>
                          )}
                        </button>
                        <div className="border-t border-white/10 my-1" />
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onDelete(character);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {character.notes && (
              <p className="text-xs text-white/50 line-clamp-2">
                {character.notes}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={() => onGenerateSeed(character)}
              className="w-full btn-future text-purple-400 border-purple-400/30 hover:bg-purple-400/10 text-xs"
            >
              <Wand2 className="w-3 h-3 mr-2" />
              Generate Seed
            </button>

            <button className="w-full btn-future text-blue-400 border-blue-400/30 hover:bg-blue-400/10 text-xs">
              <Upload className="w-3 h-3 mr-2" />
              Upload Images
            </button>
          </div>
        </CardContent>
      </HolographicCard>
    </div>
  );
};

const CharacterProfilesSection: React.FC<CharacterProfilesSectionProps> = ({ project }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterProfile | null>(null);

  const { data: charactersData, isLoading } = useCharacterProfilesQuery(project.id);
  const updateCharacterMutation = useUpdateCharacterProfileMutation();
  const deleteCharacterMutation = useDeleteCharacterProfileMutation();

  const characters: CharacterProfile[] = Array.isArray(charactersData) ? charactersData :
    charactersData?.characters ?? [];

  const handleEdit = (character: CharacterProfile) => {
    // TODO: Implement edit modal
    console.log('Edit character:', character);
  };

  const handleDelete = async (character: CharacterProfile) => {
    if (window.confirm(`Are you sure you want to delete "${character.name}"?`)) {
      try {
        await deleteCharacterMutation.mutateAsync({
          projectId: project.id,
          characterId: character.id
        });
      } catch (error) {
        console.error('Failed to delete character:', error);
      }
    }
  };

  const handleGenerateSeed = (character: CharacterProfile) => {
    setSelectedCharacter(character);
    setShowSeedModal(true);
  };

  const handleToggleDefault = async (character: CharacterProfile) => {
    try {
      await updateCharacterMutation.mutateAsync({
        projectId: project.id,
        characterId: character.id,
        data: { isDefault: !character.isDefault }
      });
    } catch (error) {
      console.error('Failed to update character default status:', error);
    }
  };

  return (
    <div>
      <HolographicCard glow="cyan" intensity="medium">
        <CardHeader
          title="CHARACTER PROFILES"
          subtitle={`${characters.length} characters`}
          icon={<Users className="w-5 h-5" />}
          actions={
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Character
            </button>
          }
        />

        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-[9/16]" />
                  <Skeleton className="h-4" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          ) : characters.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {characters.map((character) => (
                <CharacterCard
                  key={character.id}
                  character={character}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onGenerateSeed={handleGenerateSeed}
                  onToggleDefault={handleToggleDefault}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-light text-white/70 mb-2">
                No Characters Yet
              </h3>
              <p className="text-white/40 mb-6 max-w-md mx-auto">
                Create character profiles with seed images to use in your pipeline executions.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Character
              </button>
            </div>
          )}
        </CardContent>
      </HolographicCard>

      {/* Create Character Modal */}
      <CreateCharacterModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        project={project}
      />

      {/* Seed Generation Modal */}
      {selectedCharacter && (
        <SeedGenerationModal
          isOpen={showSeedModal}
          onClose={() => {
            setShowSeedModal(false);
            setSelectedCharacter(null);
          }}
          project={project}
          character={selectedCharacter}
        />
      )}
    </div>
  );
};

export default CharacterProfilesSection;